import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CircularProgress } from "@mui/material";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useServices } from "../../contexts/ServicesContext/projectservices.context";
import { BudgetItemTypeEnum } from "../../types/budget/budget";
import { ServiceOptions } from "../../utils/serviceTypesFromBudget";
import { budgetToDraft } from "./domain/persist";
import { useEstudoAutosave } from "./useEstudoAutosave";
import {
  applyKeyChange,
  calcLine,
  createItem,
  deleteItem,
  effectiveTax,
  emptyDraft,
  formatBRL,
  isFornecedorADefinir,
  linkedCount,
  moveItemBy,
  PAYMENT_FORMS,
  pendenciaText,
  reorderDraft,
  setTaxMode,
  setTaxValue,
  setTextField,
  TAX_COLUMNS,
  totalsBoard,
  type DraftItem,
  type DraftState,
  type TaxKey,
  type TextField,
} from "./domain/draft";
import SupplierField from "./SupplierField";
import {
  AddLine,
  Badge,
  Combo,
  Field,
  Footer,
  Handle,
  Inactive,
  Lead,
  Linked,
  Note,
  Notes,
  Page,
  Remove,
  Save,
  Scroll,
  Select,
  Sub,
  Table,
  TableWrap,
  Title,
  TopBand,
  Totals,
} from "./styles";

const CATEGORIES = Object.entries(BudgetItemTypeEnum)
  .filter(([key]) => key !== "TRAVEL_INSURANCE")
  .map(([value, label]) => ({ value, label: String(label) }))
  .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

const SHARED_NOTE =
  "Vale para todos os itens com a mesma forma de pagamento, fornecedor e categoria.";
const ITEM_NOTE =
  "Ao criar o orçamento, este valor será parametrizado individualmente no item (menu do serviço › Taxas).";

const GRIP = (
  <svg
    width="14"
    height="18"
    viewBox="0 0 14 18"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="4" cy="3" r="1.6" />
    <circle cx="10" cy="3" r="1.6" />
    <circle cx="4" cy="9" r="1.6" />
    <circle cx="10" cy="9" r="1.6" />
    <circle cx="4" cy="15" r="1.6" />
    <circle cx="10" cy="15" r="1.6" />
  </svg>
);

function categoryLabel(key: string): string {
  return (BudgetItemTypeEnum as Record<string, string>)[key] || key;
}

function serviceChoices(
  category: string
): Array<{ text: string; value: string }> {
  const list = (ServiceOptions[category] || []) as Array<{
    text: string;
    value: string;
  }>;
  return list.filter((option) => option.value);
}

function dropPlace(event: DragOverEvent | DragEndEvent): {
  id: string;
  place: "before" | "after";
} | null {
  if (!event.over) return null;
  const overRect = event.over.rect;
  const activeRect =
    event.active.rect.current.translated ?? event.active.rect.current.initial;
  if (!overRect || !activeRect) {
    return { id: String(event.over.id), place: "before" };
  }
  const activeMid = activeRect.top + activeRect.height / 2;
  const overMid = overRect.top + overRect.height / 2;
  return {
    id: String(event.over.id),
    place: activeMid > overMid ? "after" : "before",
  };
}

type RowProps = {
  item: DraftItem;
  index: number;
  draft: DraftState;
  dragging: boolean;
  drop: { id: string; place: "before" | "after" } | null;
  onText: (id: string, field: TextField, value: string) => void;
  onKey: (
    id: string,
    field: "cat" | "forma" | "forn",
    value: string,
    extra?: { categoryLabel?: string; supplierName?: string }
  ) => void;
  onTax: (id: string, key: TaxKey, value: string) => void;
  onTaxMode: (id: string, key: TaxKey, mode: "%" | "R$") => void;
  onDelete: (id: string) => void;
  onMove: (id: string, delta: -1 | 1) => void;
  onEnterValue: (id: string) => void;
};

function DataRow({
  item,
  index,
  draft,
  dragging,
  drop,
  onText,
  onKey,
  onTax,
  onTaxMode,
  onDelete,
  onMove,
  onEnterValue,
}: RowProps) {
  const { attributes, listeners, setNodeRef } = useSortable({ id: item.id });
  const n = index + 1;
  const calc = calcLine(draft, item);
  const services = serviceChoices(item.cat);
  const linked = linkedCount(draft, item);
  const dropClass =
    drop?.id === item.id
      ? drop.place === "after"
        ? "drop-after"
        : "drop-before"
      : "";

  return (
    <tr
      ref={setNodeRef}
      className={`${dragging ? "dragging" : ""} ${dropClass}`.trim()}
    >
      <td className="s0">
        <Handle
          type="button"
          aria-label={`Reordenar item ${n}. Arraste ou use as setas para cima e para baixo`}
          title="Arraste para reordenar"
          data-draft-id={item.id}
          data-draft-field="rh"
          {...attributes}
          {...listeners}
          onKeyDown={(event) => {
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
            event.preventDefault();
            event.stopPropagation();
            onMove(item.id, event.key === "ArrowUp" ? -1 : 1);
          }}
        >
          {GRIP}
        </Handle>
      </td>
      <td className="s1">{n}</td>
      <td>
        <Select
          aria-label={`Categoria, item ${n}`}
          data-draft-id={item.id}
          data-draft-field="cat"
          value={item.cat}
          onChange={(event) =>
            onKey(item.id, "cat", event.target.value, {
              categoryLabel: categoryLabel(event.target.value),
            })
          }
        >
          <option value="">—</option>
          {CATEGORIES.map((category) => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
          {item.cat &&
            !CATEGORIES.some((category) => category.value === item.cat) && (
              <option value={item.cat}>{categoryLabel(item.cat)}</option>
            )}
        </Select>
      </td>
      <td>
        <Select
          className={item.cat && !item.svc ? "warn" : ""}
          aria-label={`Serviço, item ${n}`}
          data-draft-id={item.id}
          data-draft-field="svc"
          value={item.svc}
          disabled={!item.cat}
          onChange={(event) => onText(item.id, "svc", event.target.value)}
        >
          <option value="">{item.cat ? "Selecionar…" : "—"}</option>
          {services.map((service) => (
            <option key={service.value} value={service.value}>
              {service.text}
            </option>
          ))}
        </Select>
      </td>
      <td>
        <Field
          aria-label={`Descrição, item ${n}`}
          placeholder="Descrição"
          data-draft-id={item.id}
          data-draft-field="desc"
          value={item.desc}
          onChange={(event) => onText(item.id, "desc", event.target.value)}
        />
      </td>
      <td>
        <Field
          className="num"
          inputMode="decimal"
          aria-label={`Quantidade, item ${n}`}
          data-draft-id={item.id}
          data-draft-field="qtd"
          value={item.qtd}
          onChange={(event) => onText(item.id, "qtd", event.target.value)}
        />
      </td>
      <td>
        <Field
          className="num"
          inputMode="decimal"
          aria-label={`Diária, item ${n}`}
          data-draft-id={item.id}
          data-draft-field="dias"
          value={item.dias}
          onChange={(event) => onText(item.id, "dias", event.target.value)}
        />
      </td>
      <td>
        <Field
          className="num"
          inputMode="decimal"
          aria-label={`Valor unitário, item ${n}`}
          placeholder="0,00"
          data-draft-id={item.id}
          data-draft-field="valor"
          value={item.valor}
          onChange={(event) => onText(item.id, "valor", event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            onEnterValue(item.id);
          }}
        />
      </td>
      <td style={{ textAlign: "right", fontWeight: 600 }}>
        {formatBRL(calc.tar)}
      </td>
      <td>
        <Select
          className={item.forma === "SF" ? "warn" : ""}
          aria-label={`Forma de pagamento, item ${n}`}
          data-draft-id={item.id}
          data-draft-field="forma"
          value={item.forma}
          onChange={(event) => onKey(item.id, "forma", event.target.value)}
        >
          {PAYMENT_FORMS.map((form) => (
            <option key={form.k} value={form.k}>
              {form.l}
            </option>
          ))}
        </Select>
      </td>
      {TAX_COLUMNS.map((column) => {
        if (item.forma === "SF") {
          return (
            <td key={column.k} className="tx">
              <Inactive>Campo inativo</Inactive>
            </td>
          );
        }
        const tax = effectiveTax(draft, item, column.k);
        const has = !!tax?.v;
        return (
          <td key={column.k} className="tx">
            <Combo>
              <Select
                aria-label={`Tipo de ${column.l}, item ${n}`}
                value={tax?.mode || "%"}
                onChange={(event) =>
                  onTaxMode(item.id, column.k, event.target.value as "%" | "R$")
                }
              >
                <option value="%">%</option>
                <option value="R$">R$</option>
              </Select>
              <Field
                className={`num${has ? " own" : ""}${
                  column.shared ? " lnk" : ""
                }`}
                inputMode="decimal"
                placeholder="0"
                aria-label={`${column.l}, item ${n}`}
                title={column.shared ? SHARED_NOTE : ITEM_NOTE}
                value={tax?.v || ""}
                onChange={(event) =>
                  onTax(item.id, column.k, event.target.value)
                }
              />
            </Combo>
            {column.shared && linked > 1 && (
              <Linked>↔ aplicado a {linked} itens</Linked>
            )}
          </td>
        );
      })}
      <td style={{ textAlign: "right", fontWeight: 700, color: "#13427d" }}>
        {item.forma === "SF" ? (
          <Inactive>Campo inativo</Inactive>
        ) : (
          formatBRL(calc.geral)
        )}
      </td>
      <td style={{ minWidth: 220 }}>
        <SupplierField
          draftId={item.id}
          supplierId={item.forn}
          supplierName={item.fornName}
          warn={isFornecedorADefinir(item)}
          label={`Fornecedor, item ${n}`}
          onSelect={(id, name) =>
            onKey(item.id, "forn", id, { supplierName: name })
          }
        />
      </td>
      <td>
        <Remove
          type="button"
          aria-label={`Excluir item ${n}`}
          onClick={() => onDelete(item.id)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </Remove>
      </td>
    </tr>
  );
}

export default function EstudoDeVerba() {
  const {
    activeService,
    updateService,
    activeServiceDispatch,
    loadingService,
  } = useServices();
  const location = useLocation();
  const projectId = new URLSearchParams(location.search).get("projectId") || "";
  const [draft, setDraftState] = useState<DraftState>(emptyDraft());
  const [hydratedId, setHydratedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [drop, setDrop] = useState<{
    id: string;
    place: "before" | "after";
  } | null>(null);
  const dirtyRef = useRef(false);
  const draftRef = useRef(draft);
  const serviceRef = useRef(activeService);
  const focusRef = useRef<{ id: string; field: string } | null>(null);
  const savingRef = useRef(false);

  const replaceDraft = useCallback((next: DraftState) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);

  const { markEdited, flush } = useEstudoAutosave({
    draftRef,
    setDraft: replaceDraft,
    activeService,
    serviceRef,
    dirtyRef,
    updateService,
    dispatchSaved: (saved) => {
      activeServiceDispatch({
        type: "NEW_ACTIVE_BUDGET",
        payload: { ...saved, isApiUpdate: true },
      });
    },
    enabled: Boolean(activeService?._id),
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  useEffect(() => {
    if (!activeService?._id || dirtyRef.current) return;
    if (hydratedId === activeService._id) return;
    setHydratedId(activeService._id);
    replaceDraft(budgetToDraft(activeService));
  }, [activeService, hydratedId, replaceDraft]);

  useLayoutEffect(() => {
    const target = focusRef.current;
    if (!target) return;
    const el = document.querySelector(
      `[data-draft-id="${target.id}"][data-draft-field="${target.field}"]`
    ) as HTMLElement | null;
    focusRef.current = null;
    if (!el) return;
    el.focus();
    if (el instanceof HTMLInputElement) {
      const length = el.value.length;
      try {
        el.setSelectionRange(length, length);
      } catch {
        /* campos numéricos sem seleção */
      }
    }
  });

  const commit = (
    next: DraftState,
    message?: string | null,
    focus?: { id: string; field: string }
  ) => {
    if (focus) focusRef.current = focus;
    replaceDraft(next);
    markEdited();
    if (message) toast.info(message);
  };

  const addLine = () => {
    const created = createItem(draftRef.current);
    focusRef.current = { id: created.item.id, field: "cat" };
    replaceDraft(created.draft);
    markEdited();
  };

  const board = totalsBoard(draft);
  const pendencias = pendenciaText(draft.items);

  const save = async () => {
    if (!activeService?._id || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const ok = await flush("manual");
    savingRef.current = false;
    setSaving(false);
    if (ok) toast.success("Rascunho salvo.");
  };

  if (!projectId) {
    return (
      <Page>
        <Scroll>
          <Title>
            Estudo de Verba / Rascunho de Evento <Badge>Produção</Badge>
          </Title>
          <Lead>Salve o projeto para lançar o estudo de verba.</Lead>
        </Scroll>
      </Page>
    );
  }

  if (loadingService || !activeService?._id) {
    return (
      <Page>
        <Scroll>
          <CircularProgress size={28} />
        </Scroll>
      </Page>
    );
  }

  return (
    <Page>
      <Scroll>
        <div>
          <Title>
            Estudo de Verba / Rascunho de Evento <Badge>Produção</Badge>
          </Title>
          <Lead>
            Lance tudo em formato de planilha. Use + Linha para incluir um item.
            Arraste pela alça ⋮⋮ (ou use as setas ↑ ↓ na alça) para reordenar as
            linhas.
          </Lead>
        </div>
        <TopBand>
          <Notes>
            <Note $tone="warn" role="note">
              <strong>Rascunho exclusivo para Produção.</strong> Este rascunho
              não se aplica a orçamentos de Logística.
            </Note>
            <Note $tone="info" role="note">
              <strong>Markup/Over, Imposto s/ Markup/Over e Comissão</strong>{" "}
              são informados por item. Ao criar o orçamento a partir deste
              rascunho, os valores importados serão parametrizados
              individualmente em cada item, no menu do serviço › Taxas.
            </Note>
            <Note $tone="param" role="note">
              <strong>◆ Taxa de Serviço, ISS, Imposto e Taxa MM</strong> são
              parametrizados por forma de pagamento, fornecedor e categoria,
              como no orçamento. Os campos são liberados ao escolher a forma de
              pagamento. Ao escolher a categoria, os valores passam a valer para
              todos os itens da mesma combinação (indicados com ↔ e borda
              verde): o próximo item com a mesma forma, fornecedor e categoria
              já puxa as taxas do primeiro.
            </Note>
          </Notes>
          <Totals aria-label="Totais por forma de pagamento">
            <h3>Totais</h3>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Forma de pagamento</th>
                    <th>Tarifa</th>
                    <th>Taxa</th>
                    <th>Imposto</th>
                    <th>Receita</th>
                    <th>Geral</th>
                  </tr>
                </thead>
                <tbody>
                  {board.rows.map((row) => (
                    <tr key={row.key}>
                      <th scope="row">
                        {row.label}
                        {row.sub ? <Sub>{row.sub}</Sub> : null}
                      </th>
                      <td>{formatBRL(row.sums.tar)}</td>
                      <td>{row.onlyTariff ? "" : formatBRL(row.sums.taxa)}</td>
                      <td>
                        {row.onlyTariff ? "" : formatBRL(row.sums.imposto)}
                      </td>
                      <td>
                        {row.onlyTariff ? "" : formatBRL(row.sums.receita)}
                      </td>
                      <td>
                        <strong>{formatBRL(row.sums.geral)}</strong>
                      </td>
                    </tr>
                  ))}
                  <tr className="g">
                    <th scope="row">Geral</th>
                    <td>{formatBRL(board.geral.tar)}</td>
                    <td>{formatBRL(board.geral.taxa)}</td>
                    <td>{formatBRL(board.geral.imposto)}</td>
                    <td>{formatBRL(board.geral.receita)}</td>
                    <td>{formatBRL(board.geral.geral)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Totals>
        </TopBand>

        <TableWrap>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(event) => setActiveDragId(String(event.active.id))}
            onDragOver={(event) => setDrop(dropPlace(event))}
            onDragCancel={() => {
              setActiveDragId(null);
              setDrop(null);
            }}
            onDragEnd={(event) => {
              const target = dropPlace(event);
              setActiveDragId(null);
              setDrop(null);
              if (!target) return;
              const id = String(event.active.id);
              const next = reorderDraft(
                draftRef.current,
                id,
                target.id,
                target.place
              );
              if (next === draftRef.current) return;
              focusRef.current = { id, field: "rh" };
              replaceDraft(next);
              markEdited();
            }}
          >
            <Table>
              <caption
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                }}
              >
                Itens do Estudo de Verba
              </caption>
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: 48 }} />
                <col style={{ width: 190 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 240 }} />
                <col style={{ width: 76 }} />
                <col style={{ width: 76 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 320 }} />
                {TAX_COLUMNS.map((column) => (
                  <col key={column.k} style={{ width: 200 }} />
                ))}
                <col style={{ width: 130 }} />
                <col style={{ width: 240 }} />
                <col style={{ width: 44 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="s0" rowSpan={2}>
                    <span
                      style={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                      }}
                    >
                      Reordenar
                    </span>
                  </th>
                  <th className="s1" rowSpan={2}>
                    Item
                  </th>
                  <th rowSpan={2}>Categoria</th>
                  <th rowSpan={2}>Serviço</th>
                  <th rowSpan={2}>Descrição</th>
                  <th className="r" rowSpan={2}>
                    Qtd
                  </th>
                  <th className="r" rowSpan={2}>
                    Diária
                  </th>
                  <th className="r" rowSpan={2}>
                    Valor Unitário
                  </th>
                  <th className="r" rowSpan={2}>
                    Total
                  </th>
                  <th rowSpan={2}>Forma de pagamento</th>
                  <th className="grp" colSpan={2}>
                    Taxas
                  </th>
                  <th className="grp" colSpan={1}>
                    Impostos
                  </th>
                  <th className="grp" colSpan={4}>
                    Receita
                  </th>
                  <th className="r" rowSpan={2}>
                    Total Geral
                  </th>
                  <th rowSpan={2}>Fornecedor</th>
                  <th rowSpan={2}>
                    <span
                      style={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                      }}
                    >
                      Ações
                    </span>
                  </th>
                </tr>
                <tr>
                  {TAX_COLUMNS.map((column) => (
                    <th
                      key={column.k}
                      className="txh"
                      title={column.shared ? SHARED_NOTE : ITEM_NOTE}
                    >
                      {column.l}
                      {column.shared ? " ◆" : " ⓘ"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <SortableContext
                  items={draft.items.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {draft.items.map((item, index) => (
                    <DataRow
                      key={item.id}
                      item={item}
                      index={index}
                      draft={draft}
                      dragging={activeDragId === item.id}
                      drop={drop}
                      onText={(id, field, value) =>
                        commit(setTextField(draftRef.current, id, field, value))
                      }
                      onKey={(id, field, value, extra) => {
                        const result = applyKeyChange(
                          draftRef.current,
                          id,
                          field,
                          value,
                          extra
                        );
                        commit(result.draft, result.message, { id, field });
                      }}
                      onTax={(id, key, value) =>
                        commit(setTaxValue(draftRef.current, id, key, value))
                      }
                      onTaxMode={(id, key, mode) =>
                        commit(setTaxMode(draftRef.current, id, key, mode))
                      }
                      onDelete={(id) =>
                        commit(deleteItem(draftRef.current, id))
                      }
                      onMove={(id, delta) => {
                        const next = moveItemBy(draftRef.current, id, delta);
                        if (next === draftRef.current) return;
                        commit(next, null, { id, field: "rh" });
                      }}
                      onEnterValue={() => addLine()}
                    />
                  ))}
                </SortableContext>
              </tbody>
              <tfoot>
                <tr>
                  <td className="s0" />
                  <td className="s1" />
                  <td colSpan={3}>
                    <AddLine type="button" onClick={addLine}>
                      + Linha
                    </AddLine>
                  </td>
                  <td
                    colSpan={3}
                    style={{
                      textAlign: "right",
                      fontSize: 12,
                      fontWeight: 400,
                      color: "#4a5b72",
                    }}
                  >
                    {draft.items.length} item(ns)
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatBRL(board.tariffTotal)}
                  </td>
                  <td colSpan={8} />
                  <td style={{ textAlign: "right", color: "#13427d" }}>
                    {formatBRL(board.geral.geral)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </Table>
          </DndContext>
        </TableWrap>
      </Scroll>
      <Footer>
        <span style={{ fontWeight: 600, color: "#14243a" }}>
          Estudo de Verba
        </span>
        <span>
          Itens <strong>{draft.items.length}</strong>
        </span>
        <span
          style={{
            fontWeight: 600,
            color: pendencias.tone === "ok" ? "#1F7A44" : "#9A5A00",
          }}
        >
          {pendencias.text}
        </span>
        <span>
          Valor total <strong>{formatBRL(board.geral.geral)}</strong>
        </span>
        <Save type="button" onClick={save} disabled={saving}>
          {saving ? "Salvando…" : "Salvar"}
        </Save>
      </Footer>
    </Page>
  );
}
